import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from '../../store/store';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  setResponseText as setResponseTextStore,
  setResponseVoiceType as setResponseVoiceTypeStore,
  setAddMessage as setAddMessageStore,
  setInMessage as setInMessageStore,
  setAttachMedia as setAttachMediaStore,
  setIncomingNotice as setIncomingNoticeStore,
} from '../../store/setupUserSlice';
import { v2apiBlob } from '../../api/axios';
import Log from '../../lib/log';

// ログの初期化
const log = new Log('SetupIncomingSettings');

type onChangeEvent = React.ChangeEvent<HTMLInputElement>;

interface Props {
  valid: (value: boolean) => void;
}
export default function SetupIncomingSettings(props: Props) {
  log.putLog('info', 'SetupIncomingSettings started.');
  const dispatch = useDispatch();
  const responseTextRef = useRef<HTMLInputElement>(null);
  const [responseText, setResponseText] = useState(
    useSelector((state) => state.setupUser.responseText),
  );
  const [responseTextError, setResponseTextError] = useState(false);
  const [responseVoiceType, setResponseVoiceType] = useState(
    useSelector((state) => state.setupUser.responseVoiceType),
  );
  const [addMessage, setAddMessage] = useState(
    useSelector((state) => state.setupUser.addMessage),
  );
  const [inMessage, setInMessage] = useState(
    useSelector((state) => state.setupUser.inMessage),
  );
  const [attachMedia, setAttachMedia] = useState(
    useSelector((state) => state.setupUser.attachMedia),
  );
  const [incomingNotice, setIncomingNotice] = useState(
    useSelector((state) => state.setupUser.incomingNotice),
  );
  const [isValid, setIsValid] = useState(false);

  // 入力値の検証
  const formValidation = (): boolean => {
    let valid = true;

    const responseTextCurrent = responseTextRef?.current;
    if (responseTextCurrent) {
      const ok = responseTextCurrent.validity.valid;
      setResponseTextError(!ok);
      valid &&= ok;
      dispatch(setResponseTextStore(responseTextCurrent.value));
    }

    dispatch(setResponseVoiceTypeStore(responseVoiceType));
    dispatch(setAddMessageStore(addMessage));
    dispatch(setInMessageStore(inMessage));
    dispatch(setAttachMediaStore(attachMedia));
    dispatch(setIncomingNoticeStore(incomingNotice));

    setIsValid(valid);
    return valid;
  };

  useEffect(() => {
    // 初回に一回検証する
    formValidation();
  });

  useEffect(() => {
    props.valid(isValid);
  }, [props, isValid]);

  const handleChangeResponseText = (e: onChangeEvent) => {
    setResponseText(e.target.value);
    formValidation();
  };

  const handleChangeResponseVoiceType = (e: SelectChangeEvent) => {
    setResponseVoiceType(Number(e.target.value));
    formValidation();
  };

  const handleChangeAddMessage = (e: onChangeEvent) => {
    setAddMessage(Number(e.target.checked));
    formValidation();
  };

  const handleChangeInMessage = (e: onChangeEvent) => {
    setInMessage(Number(e.target.checked));
    formValidation();
  };

  const handleChangeAttachMedia = (e: onChangeEvent) => {
    setAttachMedia(Number(e.target.checked));
    formValidation();
  };

  const handleChangeIncomingNotice = (e: onChangeEvent) => {
    setIncomingNotice(Number(e.target.checked));
    formValidation();
  };

  // 再生ボタン
  const [voiceTypeDisable, setVoiceTypeDisable] = useState(false);
  const handlePlayClick = async (e: any) => {
    setVoiceTypeDisable(true);
    // 音声データに変換
    const postData = {
      text: responseText,
      voiceType: responseVoiceType.toString(),
    };
    const res = await v2apiBlob.post('/text-to-speech', postData);
    const blobUrl = URL.createObjectURL(new Blob([res.data]));
    const audio = new Audio(blobUrl);
    audio.addEventListener('ended', (e: any) => {
      setVoiceTypeDisable(false);
    });
    audio.load();
    audio.play();
  };

  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
      }}
    >
      <Typography variant='h6' gutterBottom component='div' color={'gray'}>
        応答メッセージや、各種オプションを設定します。この内容は、後ほど変更することができます。
      </Typography>
      <Grid container spacing={2} alignContent='center'>
        <Grid item xs={12} md={9} lg={9}>
          <TextField
            error={responseTextError}
            inputProps={{ minLength: 1 }}
            inputRef={responseTextRef}
            fullWidth
            id='response'
            multiline
            rows={10}
            value={responseText}
            onChange={handleChangeResponseText}
          />
        </Grid>
        <Grid item xs={12} md={3} lg={3}>
          <Stack spacing={2}>
            <FormControl sx={{ m: 0, minWidth: '100%' }} size='small'>
              <InputLabel id='voice-type-select'>性別</InputLabel>
              <Select
                labelId='voice-type-select'
                id='voice-type-select'
                value={responseVoiceType.toString()}
                label='性別'
                onChange={handleChangeResponseVoiceType}
              >
                <MenuItem value={0}>男性</MenuItem>
                <MenuItem value={1}>女性</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={handlePlayClick}
              disabled={voiceTypeDisable}
            >
              テスト
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          <Stack spacing={1}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={addMessage === 1}
                    onChange={handleChangeAddMessage}
                  />
                }
                label={
                  <Typography fontSize={14}>
                    音声認識確認メッセージの追加
                  </Typography>
                }
              />
            </FormGroup>
            <Typography fontSize={14} color='GrayText'>
              このスイッチをONにすると、応答メッセージのあとに音声認識がされる旨のガイダンスが流れます。
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={incomingNotice === 1}
                    onChange={handleChangeIncomingNotice}
                  />
                }
                label={<Typography fontSize={14}>着信通知</Typography>}
              />
            </FormGroup>
            <Typography fontSize={14} color='GrayText'>
              このスイッチをONにすると、着信があった時点でメールで通知がされます。音声が録音されなくても着信通知は送信されます。
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={inMessage === 1}
                    onChange={handleChangeInMessage}
                  />
                }
                label={
                  <Typography fontSize={14}>
                    認識結果をメールに記載する
                  </Typography>
                }
              />
            </FormGroup>
            <Typography fontSize={14} color='GrayText'>
              このスイッチをONにすると、音声認識された結果がメールの本文に記載されます。
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={attachMedia === 1}
                    onChange={handleChangeAttachMedia}
                  />
                }
                label={
                  <Typography fontSize={14}>音声データを添付する</Typography>
                }
              />
            </FormGroup>
            <Typography fontSize={14} color='GrayText'>
              このスイッチをONにすると、音声データがメールに添付されます。
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}
